import type { Permission } from "@/types";

export const DIARY_REPORT_SECTION_IDS = [
  "header",
  "weather",
  "tasks",
  "occurrences",
  "team",
  "materials",
  "equipment",
  "photos",
  "signatures",
] as const;

export type DiaryReportSectionId = (typeof DIARY_REPORT_SECTION_IDS)[number];

export const LOCKED_DIARY_REPORT_SECTION_IDS = ["header", "weather"] as const;

export type DiaryReportTasksOptions = {
  groupByWorkFront: boolean;
};

export type DiaryReportOccurrencesOptions = {
  criticalOnly: boolean;
  hideIfEmpty: boolean;
};

export type DiaryReportPhotosOptions = {
  gridColumns: 2 | 3;
  includeCaptions: boolean;
};

export type DiaryReportSectionOptions = {
  groupByWorkFront?: boolean;
  criticalOnly?: boolean;
  hideIfEmpty?: boolean;
  gridColumns?: 2 | 3;
  includeCaptions?: boolean;
};

export type DiaryReportSection = {
  id: DiaryReportSectionId;
  enabled: boolean;
  title: string;
  options?: DiaryReportSectionOptions;
};

export type DiaryReportModelConfig = {
  renderer?: "legacy_v1" | "professional_v2";
  sections: DiaryReportSection[];
  includePhotos: boolean;
  showSignaturePlaceholders: boolean;
};

export const DEFAULT_TASKS_OPTIONS: DiaryReportTasksOptions = {
  groupByWorkFront: true,
};

export const DEFAULT_OCCURRENCES_OPTIONS: DiaryReportOccurrencesOptions = {
  criticalOnly: false,
  hideIfEmpty: false,
};

export const DEFAULT_PHOTOS_OPTIONS: DiaryReportPhotosOptions = {
  gridColumns: 2,
  includeCaptions: true,
};

export function getTasksSectionOptions(
  section: Pick<DiaryReportSection, "options"> | undefined,
): DiaryReportTasksOptions {
  return {
    groupByWorkFront: section?.options?.groupByWorkFront ?? DEFAULT_TASKS_OPTIONS.groupByWorkFront,
  };
}

export function getOccurrencesSectionOptions(
  section: Pick<DiaryReportSection, "options"> | undefined,
): DiaryReportOccurrencesOptions {
  return {
    criticalOnly: section?.options?.criticalOnly ?? DEFAULT_OCCURRENCES_OPTIONS.criticalOnly,
    hideIfEmpty: section?.options?.hideIfEmpty ?? DEFAULT_OCCURRENCES_OPTIONS.hideIfEmpty,
  };
}

export function getPhotosSectionOptions(
  section: Pick<DiaryReportSection, "options"> | undefined,
): DiaryReportPhotosOptions {
  return {
    gridColumns: section?.options?.gridColumns ?? DEFAULT_PHOTOS_OPTIONS.gridColumns,
    includeCaptions: section?.options?.includeCaptions ?? DEFAULT_PHOTOS_OPTIONS.includeCaptions,
  };
}

export type DiaryReportModelDto = {
  id: string;
  modelRef: string;
  kind: "system" | "custom";
  name: string;
  config: DiaryReportModelConfig;
  version: number;
  active: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export const PROJECT_REPORT_TYPE_KEYS = [
  "diary",
  "equipment",
  "labor",
  "occurrences",
  "progress",
  "materials",
  "photos",
  "weather",
  "checklists",
  "measurement",
  "costVsBudget",
  "clientProposal",
  "purchaseOrders",
  "payables",
  "receivables",
  "cashFlow",
] as const;

export type ProjectReportTypeKey = (typeof PROJECT_REPORT_TYPE_KEYS)[number];

export const RANGE_PROJECT_REPORT_TYPE_KEYS = [
  "equipment",
  "labor",
  "occurrences",
  "progress",
  "materials",
  "photos",
  "weather",
  "checklists",
  "measurement",
  "costVsBudget",
  "purchaseOrders",
  "payables",
  "receivables",
  "cashFlow",
] as const;

export type RangeProjectReportTypeKey = (typeof RANGE_PROJECT_REPORT_TYPE_KEYS)[number];

export const EXCEL_PROJECT_REPORT_TYPE_KEYS = [
  "equipment",
  "labor",
  "occurrences",
  "progress",
  "materials",
  "weather",
  "checklists",
  "measurement",
  "costVsBudget",
  "purchaseOrders",
  "payables",
  "receivables",
  "cashFlow",
] as const;

export type ExcelProjectReportTypeKey = (typeof EXCEL_PROJECT_REPORT_TYPE_KEYS)[number];

export const PROJECT_REPORT_GROUPS = [
  "execution",
  "commercial",
  "purchasesFinance",
] as const;

export type ProjectReportGroup = (typeof PROJECT_REPORT_GROUPS)[number];

export const PROJECT_REPORT_TYPES_BY_GROUP: Record<ProjectReportGroup, ProjectReportTypeKey[]> = {
  execution: [
    "diary",
    "equipment",
    "labor",
    "occurrences",
    "progress",
    "materials",
    "photos",
    "weather",
    "checklists",
  ],
  commercial: ["measurement", "costVsBudget", "clientProposal"],
  purchasesFinance: ["purchaseOrders", "payables", "receivables", "cashFlow"],
};

export const PROJECT_REPORT_EXTRA_PERMISSION: Partial<Record<ProjectReportTypeKey, Permission>> = {
  photos: "project.photos.view",
  measurement: "project.budgets.view",
  costVsBudget: "project.finance.view",
  clientProposal: "project.clientBudgets.view",
  purchaseOrders: "project.purchaseOrders.view",
  payables: "finance.payables.view",
  receivables: "finance.receivables.view",
  cashFlow: "finance.cashFlow.view",
};

export type ProjectReportDto = {
  id: string;
  projectId: string;
  reportType: ProjectReportTypeKey;
  projectDiaryEntryId: string | null;
  modelRef: string;
  modelName: string;
  modelVersion: number;
  statusKey: "pending" | "processing" | "done" | "error";
  storagePath: string | null;
  errorMessage: string | null;
  dateFrom: string;
  dateTo: string;
  createdAt: string;
  outputFormat?: "pdf" | "xlsx";
};

export type GenerateDiaryProjectReportInput = {
  reportType: "diary";
  dateFrom: string;
  dateTo: string;
  modelRef?: string;
  includePhotos?: boolean;
};

export type GenerateClientProposalReportInput = {
  reportType: "clientProposal";
  clientBudgetId: string;
};

export type GenerateRangeProjectReportInput = {
  reportType: RangeProjectReportTypeKey;
  dateFrom: string;
  dateTo: string;
  format?: "pdf" | "xlsx";
};

export type GenerateProjectReportInput =
  | GenerateDiaryProjectReportInput
  | GenerateClientProposalReportInput
  | GenerateRangeProjectReportInput;
